import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, Flame } from "lucide-react";
import { NAV } from "@/lib/nav";
import { Logo } from "@/components/brand/Logo";
import { useFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();
  const { data: ministry } = useFetch("/ministry");
  const logo = ministry?.logo;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-navy/95 backdrop-blur-xl border-b border-white/10" : "bg-navy/70 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="nav-home-logo" className="shrink-0">
          <Logo variant="light" src={logo} />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => setOpenGroup(item.label)}
              onMouseLeave={() => setOpenGroup(null)}
            >
              <Link
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={`flex items-center gap-1 px-3 py-2 font-display text-[13px] font-medium uppercase tracking-[0.12em] transition-colors ${
                  location.pathname === item.to ? "text-brand-orange" : "text-white/80 hover:text-white"
                }`}
              >
                {item.label}
                {item.children && <ChevronDown className="h-3.5 w-3.5" />}
              </Link>
              {item.children && openGroup === item.label && (
                <div className="absolute left-0 top-full w-60 border border-white/10 bg-navy-surface py-2 shadow-2xl">
                  {item.children.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      data-testid={`nav-sublink-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="block px-4 py-2.5 text-sm text-white/75 transition-colors hover:bg-white/5 hover:text-brand-orange"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button
            asChild
            data-testid="nav-give-cta"
            className="rounded-sm bg-brand-orange px-6 font-display text-xs font-semibold uppercase tracking-[0.14em] text-white transition-transform duration-300 hover:scale-105 hover:bg-brand-orange-600"
          >
            <Link to="/giving">
              <Flame className="mr-2 h-4 w-4" /> Partner With Us
            </Link>
          </Button>
        </div>

        {/* Mobile */}
        <Sheet>
          <SheetTrigger asChild className="lg:hidden">
            <button data-testid="mobile-menu-trigger" aria-label="Open menu" className="text-white">
              <Menu className="h-7 w-7" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[86%] max-w-sm border-white/10 bg-navy p-0 text-white">
            <div className="border-b border-white/10 p-5">
              <Logo variant="light" src={logo} />
            </div>
            <div className="no-scrollbar max-h-[calc(100vh-160px)] overflow-y-auto p-4">
              {NAV.map((item) => (
                <div key={item.label} className="py-1">
                  <SheetClose asChild>
                    <Link
                      to={item.to}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      className="block px-2 py-2 font-display text-sm font-semibold uppercase tracking-[0.12em] text-white"
                    >
                      {item.label}
                    </Link>
                  </SheetClose>
                  {item.children && (
                    <div className="ml-2 border-l border-white/10 pl-3">
                      {item.children.map((c) => (
                        <SheetClose asChild key={c.to}>
                          <Link
                            to={c.to}
                            className="block py-1.5 text-sm text-white/70 hover:text-brand-orange"
                          >
                            {c.label}
                          </Link>
                        </SheetClose>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 p-4">
              <SheetClose asChild>
                <Button asChild className="w-full rounded-sm bg-brand-orange font-display uppercase tracking-wide hover:bg-brand-orange-600">
                  <Link to="/giving">Partner With Us</Link>
                </Button>
              </SheetClose>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Navbar;
